import {useTheme} from "@mui/material";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import ptBR from "date-fns/locale/pt-BR";
import {timeZone} from "global/constants";
import pxToRem from "hooks/usePxToRem";
import {forwardRef, useEffect, useState} from "react";
import {useFormContext, UseFormSetValue} from "react-hook-form";
import {getAbsoluteDate, getDateMinusTimezoneAdapted} from "util/getISODate";

type Props = {
  value: any;
  name: string;
  title?: string;
  disabled?: boolean;
  required?: boolean;
  errorName?: string;
  setValue?: UseFormSetValue<any>;
  minDate?: Date;
  maxDate?: Date;
  disableFuture?: boolean;
  helperText?: string;
  errorSummaryMessage?: string;
  label?: string;
  fullWidth?: boolean;
  fontSize?: number;
};

const ProcessDatePicker = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    value,
    name,
    title = name,
    disabled = false,
    required = false,
    errorName,
    setValue: customSetValue,
    minDate,
    maxDate,
    disableFuture = false,
    helperText = "Preencha este campo.",
    errorSummaryMessage,
    label,
    fullWidth = false,
    fontSize,
  } = props;
  const {setValue, setError, clearErrors, getFieldState, formState} =
    useFormContext();
  const [hasError, setHasError] = useState(false);
  const ERROR_NAME = "processDate";
  const processDateErrorKey = `${ERROR_NAME}.${errorName ?? name}`;
  const [helperTextOnInvalid, setHelperTextOnInvalid] = useState(false);
  const theme = useTheme();

  const getProcessDateErrorMessage = () => {
    return (
      getFieldState(name, formState).error?.message ??
      errorSummaryMessage ??
      title
    );
  };

  useEffect(() => {
    if (!hasError) {
      return;
    }

    const currentProcessDateMessage =
      getFieldState(processDateErrorKey, formState).error?.message;
    const nextProcessDateMessage = getProcessDateErrorMessage();

    if (currentProcessDateMessage === nextProcessDateMessage) {
      return;
    }

    setError(processDateErrorKey, {
      message: nextProcessDateMessage,
    });
  }, [formState, getFieldState, hasError, name, processDateErrorKey, setError]);

  const conditionalSetValue = (
    name: string,
    value: any,
    options?: Partial<{
      shouldValidate: boolean;
      shouldDirty: boolean;
      shouldTouch: boolean;
    }>,
  ) => {
    if (customSetValue) {
      customSetValue(name, value, options);
    } else {
      setValue(name, value, options);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <DatePicker
        minDate={minDate}
        timezone={timeZone}
        maxDate={maxDate}
        disableFuture={disableFuture}
        label={label}
        slotProps={{
          field: {
            clearable: true,
            onClear: () => conditionalSetValue(name, null, {shouldDirty: true}),
          },
          textField: {
            size: "small",
            error: hasError,
            fullWidth: fullWidth,
            inputRef: ref,
            inputProps: {
              style: {
                fontSize: fontSize ? pxToRem(fontSize) : "default",
              },
            },
            InputLabelProps: {
              style: {
                fontSize: fontSize ?? "default",
                color: props.disabled
                  ? theme.palette.grey[300]
                  : theme.palette.grey[500],
              },
            },

            onBlur: () => setHelperTextOnInvalid(false),
            required: required,
            helperText: (hasError || helperTextOnInvalid) && helperText,
            onInvalid: () => setHelperTextOnInvalid(true),
          },
        }}
        value={
          !!value && value?.toString() !== "Invalid Date"
            ? getDateMinusTimezoneAdapted(new Date(value)?.toISOString())
            : null
        }
        format="dd/MM/yyyy"
        onError={(value) => {
          if (
            value?.toString() === "invalidDate" ||
            value?.toString() === "minDate" ||
            value?.toString() === "maxDate"
          ) {
            setHasError(true);
            setError(processDateErrorKey, {
              message: getProcessDateErrorMessage(),
            });
          } else {
            setHasError(false);
          }
        }}
        onChange={(newValue) => {
          if (
            newValue &&
            typeof newValue !== "string" &&
            newValue?.toString() !== "Invalid Date"
          ) {
            const date = getAbsoluteDate(newValue);
            conditionalSetValue(name, date, {shouldDirty: true});
            clearErrors(processDateErrorKey);
          } else if (newValue === null) {
            conditionalSetValue(name, null, {shouldDirty: true});
            clearErrors(processDateErrorKey);
          }
        }}
        disabled={disabled}
      />
    </LocalizationProvider>
  );
});

ProcessDatePicker.displayName = "ProcessDatePicker";

export default ProcessDatePicker;
